"use client";

import { useEffect, useMemo, useState } from "react";
import { Disc3, Search, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { OTHER_GENRE } from "@/lib/utils";
import { useModalZIndex } from "@/lib/use-modal-z-index";

interface GenreDrawerProps {
  genres: string[];
  genreCounts?: Map<string, number>;
  genreFilteredCount?: number;
  otherSubGenres: string[];
  selectedGenres: string[];
  onGenresChange: (genres: string[]) => void;
  genreMatchMode: "or" | "and";
  onMatchModeChange: (mode: "or" | "and") => void;
  onClose: () => void;
  jukeboxMode?: boolean;
  onPlay?: () => void;
  jukeboxReady?: boolean;
}

export function GenreDrawer({
  genres,
  genreCounts,
  genreFilteredCount,
  otherSubGenres,
  selectedGenres,
  onGenresChange,
  genreMatchMode,
  onMatchModeChange,
  onClose,
  jukeboxMode,
  onPlay,
  jukeboxReady,
}: GenreDrawerProps) {
  const t = useTranslations("Filters");
  const tj = useTranslations("Jukebox");
  const zIndex = useModalZIndex();
  const [search, setSearch] = useState("");

  const hasOther = genres.includes(OTHER_GENRE);
  const mainGenres = useMemo(
    () => genres.filter((g) => g !== OTHER_GENRE),
    [genres]
  );

  const filteredMainGenres = useMemo(() => {
    if (!search) return mainGenres;
    const q = search.toLowerCase();
    return mainGenres.filter((g) => g.toLowerCase().includes(q));
  }, [mainGenres, search]);

  const filteredOtherSubGenres = useMemo(() => {
    if (!search) return otherSubGenres;
    const q = search.toLowerCase();
    return otherSubGenres.filter((g) => g.toLowerCase().includes(q));
  }, [otherSubGenres, search]);

  const showOtherSection =
    hasOther && (filteredOtherSubGenres.length > 0 || (!search && hasOther));

  const noResults =
    filteredMainGenres.length === 0 &&
    filteredOtherSubGenres.length === 0 &&
    (!hasOther || !!search);

  const toggleGenre = (genre: string) => {
    if (selectedGenres.includes(genre)) {
      onGenresChange(selectedGenres.filter((g) => g !== genre));
    } else {
      onGenresChange([...selectedGenres, genre]);
    }
  };

  const selectedCount = selectedGenres.length;

  // Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div
      className="fixed inset-0 flex items-end md:items-stretch md:justify-end bg-black/80 backdrop-blur-sm animate-overlay-in"
      style={{ zIndex }}
      onClick={onClose}
    >
      {/* Panel */}
      <div
        className="relative flex w-full max-h-[85vh] flex-col border-t border-neutral-800 bg-surface md:w-[420px] md:border-t-0 md:border-l md:max-h-full md:h-full animate-slide-up md:animate-slide-right"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner markers */}
        <div className="absolute -top-1 -left-1 h-3 w-3 border-t border-l border-acid" />
        <div className="absolute -top-1 -right-1 h-3 w-3 border-t border-r border-acid" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid" />
        <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid" />

        {/* Header (sticky) */}
        <div className="shrink-0 border-b border-neutral-800 px-5 pt-4 pb-3 space-y-3">
          {/* Title + close */}
          <div className="flex items-center justify-between">
            <h2 className="font-mono text-sm font-bold tracking-wider text-white">
              {jukeboxMode ? tj("title") : t("allGenres").replace("ALL ", "")}
            </h2>
            <button
              onClick={onClose}
              className="shrink-0 text-neutral-500 hover:text-white transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 4L12 12M12 4L4 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("genreSearch")}
              className="w-full rounded-md border border-neutral-700 bg-dark py-2 pl-9 pr-8 font-mono text-xs text-white placeholder:text-neutral-500 focus:border-acid focus:outline-none"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {jukeboxMode && (
            <p className="font-mono text-[10px] text-neutral-500">
              {tj("audioOnly")}
            </p>
          )}

          {/* AND/OR toggle sentence + selection info (only when genres selected) */}
          {selectedCount > 0 && (
            <div className="space-y-2">
              {/* Sentence: "Show events with [ANY/ALL] of selected" */}
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-[10px] text-neutral-500">
                  {t("matchPrefix")}
                </span>
                <div className="flex gap-0.5 rounded-md border border-neutral-700 bg-dark p-0.5">
                  <button
                    onClick={() => onMatchModeChange("or")}
                    className={`rounded px-2 py-0.5 font-mono text-[10px] font-bold tracking-wider transition-colors ${
                      genreMatchMode === "or"
                        ? "bg-acid text-black"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    {t("matchAny")}
                  </button>
                  <button
                    onClick={() => onMatchModeChange("and")}
                    className={`rounded px-2 py-0.5 font-mono text-[10px] font-bold tracking-wider transition-colors ${
                      genreMatchMode === "and"
                        ? "bg-acid text-black"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    {t("matchAll")}
                  </button>
                </div>
                <span className="font-mono text-[10px] text-neutral-500">
                  {t("matchSuffix")}
                </span>
              </div>

              {/* Match count + selected count + clear */}
              <div className="flex items-center justify-between">
                {genreFilteredCount != null && (
                  <span className="font-mono text-[10px] text-neutral-500">
                    {genreFilteredCount} {genreFilteredCount === 1 ? "show" : "shows"}
                  </span>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <span className="font-mono text-[10px] text-neutral-500">
                    {t("selectedCount", { count: selectedCount })}
                  </span>
                  <button
                    onClick={() => onGenresChange([])}
                    className="font-mono text-[10px] font-bold tracking-wider text-acid hover:text-acid/80 transition-colors"
                  >
                    {t("clearAll")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 space-y-6">
          {/* Music genres section */}
          {filteredMainGenres.length > 0 && (
            <div>
              <p className="mb-3 font-mono text-[10px] font-bold tracking-wider text-neutral-500">
                {t("musicGenres")}
              </p>
              <div className="flex flex-wrap gap-2">
                {filteredMainGenres.map((genre) => {
                  const isSelected = selectedGenres.includes(genre);
                  const count = genreCounts?.get(genre);
                  return (
                    <button
                      key={genre}
                      onClick={() => toggleGenre(genre)}
                      className={`cursor-pointer rounded-full border px-3 py-1 font-mono text-xs transition-colors ${
                        isSelected
                          ? "border-acid bg-acid/10 text-acid"
                          : "border-neutral-700 text-neutral-400 hover:border-neutral-500 hover:text-neutral-300"
                      }`}
                    >
                      {genre.toUpperCase()}
                      {count != null && (
                        <span className={isSelected ? "ml-1.5 text-acid/50" : "ml-1.5 text-neutral-600"}>{count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Other section */}
          {showOtherSection && (
            <div>
              <div className="mb-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-neutral-800" />
                <p className="font-mono text-[10px] font-bold tracking-wider text-neutral-500">
                  {t("other")}
                </p>
                <div className="h-px flex-1 bg-neutral-800" />
              </div>
              <div className="flex flex-wrap gap-2">
                {filteredOtherSubGenres.map((genre) => {
                  const isSelected = selectedGenres.includes(genre);
                  return (
                    <button
                      key={genre}
                      onClick={() => toggleGenre(genre)}
                      className={`cursor-pointer rounded-full border px-3 py-1 font-mono text-xs transition-colors ${
                        isSelected
                          ? "border-acid bg-acid/10 text-acid"
                          : "border-neutral-700/50 text-neutral-500 hover:border-neutral-600 hover:text-neutral-400"
                      }`}
                    >
                      {genre.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty state */}
          {noResults && (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="font-mono text-xs text-neutral-500">
                {t("noGenresFound")}
              </p>
            </div>
          )}
        </div>

        {/* Footer (sticky) */}
        <div className="shrink-0 border-t border-neutral-800 px-5 py-3">
          {jukeboxMode && (
            <p className="mb-2 text-center font-mono text-[10px] text-neutral-500">
              {tj("footerHint")}
            </p>
          )}
          {jukeboxMode ? (
            jukeboxReady ? (
              <button
                onClick={onPlay}
                className="flex w-full items-center justify-center gap-2.5 rounded-md bg-[#1DB954] py-2.5 font-mono text-sm font-bold tracking-wider text-black transition-all hover:shadow-[0_0_20px_rgba(29,185,84,0.4)]"
              >
                <Disc3 className="h-4 w-4 animate-spin [animation-duration:3s]" />
                {tj("playJukebox")} ▶
              </button>
            ) : (
              <button
                disabled
                className="flex w-full items-center justify-center gap-2.5 rounded-md bg-neutral-800 py-2.5 font-mono text-sm font-bold tracking-wider text-neutral-500 cursor-not-allowed"
              >
                <Disc3 className="h-4 w-4" />
                {tj("selectGenres")}
              </button>
            )
          ) : onPlay ? (
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 cursor-pointer rounded-md bg-acid py-2.5 font-mono text-sm font-bold tracking-wider text-black transition-colors hover:bg-acid/90"
              >
                FILTER SHOWS
              </button>
              {jukeboxReady ? (
                <button
                  onClick={onPlay}
                  className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md bg-[#1DB954] py-2.5 font-mono text-sm font-bold tracking-wider text-black transition-all hover:shadow-[0_0_20px_rgba(29,185,84,0.4)]"
                >
                  <Disc3 className="h-4 w-4 animate-spin [animation-duration:3s]" />
                  {tj("playJukebox")} ▶
                </button>
              ) : (
                <button
                  disabled
                  className="flex flex-1 items-center justify-center gap-2 rounded-md bg-neutral-800 py-2.5 font-mono text-sm font-bold tracking-wider text-neutral-500 cursor-not-allowed"
                >
                  <Disc3 className="h-4 w-4" />
                  {tj("playJukebox")} ▶
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={onClose}
              className="w-full rounded-md bg-acid py-2.5 font-mono text-sm font-bold tracking-wider text-black transition-colors hover:bg-acid/90"
            >
              {t("done")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
